import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { getGetProfileQueryKey, useSignUp } from "../../generated/hook";
import { SignUpInboundDto } from "../../generated/model";
import Cookies from "js-cookie";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

const SignUpPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpInboundDto>({
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
    },
  });

  const {
    mutate: handleRegister,
    isError,
    isPending,
  } = useSignUp({
    mutation: {
      onSuccess: (data) => {
        if (!data.token) {
          return;
        }

        // Store JWT in cookies
        Cookies.set("token", data.token, { expires: 7 });

        // Invalidate connected user query to refetch user data
        queryClient.invalidateQueries({
          queryKey: getGetProfileQueryKey(),
        });

        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/");
        }, 1500);
      },
      onError: (error) => {
        console.error("Registration error:", error);
      },
    },
  });

  const onSubmit = async (data: SignUpInboundDto) => {
    handleRegister({ data });
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Sign Up
          </Typography>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              An error occurred during registration
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("firstName")}
              margin="normal"
              fullWidth
              id="firstName"
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              {...register("lastName")}
              margin="normal"
              fullWidth
              id="lastName"
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
            <TextField
              {...register("email")}
              margin="normal"
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              {...register("password")}
              margin="normal"
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            >
              Sign Up
            </Button>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Successfully registered!"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Container>
  );
};

export default SignUpPage;
